import { BaseProjectIdProps } from "@custom-types/base-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Stack } from "@mui/material";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { WizardLeftPanel } from "@pages/data-management/wizard-left-panel";
import { WizardRightPanel } from "@pages/data-management/wizard-right-panel";

interface Props extends BaseProjectIdProps {
  /** Main state of the Data Management workflow. See DataManagementWorkflow. */
  state: WorkflowState;
  /** The size in bytes of all selected files to upload. */
  totalFileSize: number,
  /** The upload speed in MB/s. */
  uploadSpeed: number;
  /** Upload progress value from 0 to 100. */
  uploadProgress: number;
  /** Processing progress value from 0 to 100. */
  processProgress: number;
  /** Registration progress value from 0 to 100. */
  registerProgress: number;
  /** Publishing progress value from 0 to 100. */
  publishProgress: number;
  /** boolean indicating if we have unsupported scans */
  hasUnsupportedScans: boolean;
  /** Sets the flag whether the user has closed the wizard view. In this case, the table view is shown. */
  setHasClosedWizardView: React.Dispatch<React.SetStateAction<boolean>>;
  /** Callback to set whether to prevent the redirect to the Viewer when the published state is reached. */
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Component for showing a wizard in Data Management.
 * Left panel: Pages with various info texts.
 * Right panel: The progress of the current workflow state, in particular a progress circle.
 */
export function WizardView({
  projectId,
  state,
  totalFileSize,
  uploadSpeed,
  uploadProgress,
  processProgress,
  registerProgress,
  publishProgress,
  hasUnsupportedScans,
  setHasClosedWizardView,
  setShouldPreventRedirect,
}: Props): JSX.Element {
  const { isScreenXsAndSmall } = useMediaQueryList();

  // On narrow screens, we want the left panel to be at the bottom since it's less important.
  return (
    <Stack
      direction={ isScreenXsAndSmall ? "column" : "row" }
      gap="20px"
      sx={{
        width: "100%",
        height: isScreenXsAndSmall ? "100%" : "calc(100vh - 210px)",
        minHeight: isScreenXsAndSmall ? undefined : "520px",
        marginTop: "30px",
      }}
    >
      { isScreenXsAndSmall ? undefined : <WizardLeftPanel/> }
      <WizardRightPanel
        projectId={projectId}
        state={state}
        totalFileSize={totalFileSize}
        uploadSpeed={uploadSpeed}
        uploadProgress={uploadProgress}
        processProgress={processProgress}
        registerProgress={registerProgress}
        publishProgress={publishProgress}
        hasUnsupportedScans={hasUnsupportedScans}
        setHasClosedWizardView={setHasClosedWizardView}
        setShouldPreventRedirect={setShouldPreventRedirect}
      />
      { isScreenXsAndSmall ? <WizardLeftPanel/> : undefined }
    </Stack>
  );
}
